<template>
  <div class="card">
    <transition name="modal">
      <tracking-info-modal
        v-if="showTrackingInfoModal"
        :order="order"
        @close="showTrackingInfoModal = false"
        @updateTrackingInfo="
          showTrackingInfoModal = false;
          $emit('updateTrackingInfo');
        "
      />
    </transition>

    <card-header :title="'Orden #' + order.id">
      <template #badge>
        <status-badge :status="order.status" :label="getStatus(order.status)" />
      </template>
      <template #actions>
        <button
          class="button-interactive flex items-center system-button"
          @click.prevent="collapsed = !collapsed"
        >
          <span class="text-sm mr-2">{{
            collapsed ? "Ver detalle completo" : "Colapsar"
          }}</span>
          <arrow-icon class="w-3 smooth" :class="[{ '-rotate-90': collapsed }]" />
        </button>
      </template>
    </card-header>

    <router-link
      v-if="order.diet && order.diet.pet"
      :to="{
        name: 'petsDetail',
        params: { id: order.diet.pet.id },
      }"
      class="flex items-baseline space-x-2 text-primary-500 mb-3"
    >
      <dog-icon
        v-if="order.diet.pet.breed.pet_type.slug === 'dog'"
        class="w-5 fill-current"
      />

      <cat-icon
        v-else-if="order.diet.pet.breed.pet_type.slug === 'cat'"
        class="w-5 fill-current"
      />
      <p class="font-medium">
        {{ order.diet.pet.name }}
      </p>
    </router-link>

    <div class="detail-grid">
      <detail-field label="Cliente">
        <router-link
          :to="{
            name: 'clientsDetail',
            params: { id: order.client.id },
          }"
          class="detail-link"
          >{{ order.client.name }} {{ order.client.last_name }}</router-link
        >
      </detail-field>

      <detail-field label="Método de entrega" :value="order.pickup_delivery ? 'Pick-Up' : 'Envío'" />

      <detail-field label="Semanas" :value="order.weeks" />

      <detail-field label="Fecha de creación" :value="formatDatetime(order.created_at)" />
      <detail-field label="Fecha de última actualización" :value="formatDatetime(order.updated_at)" />

      <detail-field
        label="Fecha de entrega programada"
        :value="formatDate(order.min_delivery_date)"
      />

      <detail-field
        v-if="order.delivery_date"
        label="Fecha de entrega realizada"
        :value="formatDatetime(order.delivery_date)"
      />
    </div>

    <div v-if="!order.pickup_delivery && order.address" class="mt-3 text-sm">
      <p class="font-medium">Dirección de entrega</p>
      <address-component
        :address="order.address"
        :showShippingPrice="
          ['pending', 'in_process', 'ready_for_delivery'].includes(order.status)
        "
        :branchId="order.branch_id"
      />
    </div>

    <div v-show="!collapsed">
      <div class="div-line"></div>

      <div
        v-if="order.status === 'delivered' && !order.pickup_delivery"
        class="mt-2 text-sm"
      >
        <p class="detail-label">¿Hubo algún problema al enviar la orden?</p>
        <button
          class="secondary-button block mt-1"
          @click.prevent="$emit('orderFailed')"
        >
          Solicitar al cliente nuevo método de entrega
        </button>
      </div>

      <div v-if="!order.pickup_delivery">
        <div v-if="order.shipping_price" class="mt-2">
          <detail-field label="Costo de envío" :value="integerToCurrency(order.shipping_price)" />
        </div>

        <div v-if="['delivered'].includes(order.status)">
          <section-header title="Datos de Envío" />

          <div class="card-bordered">
            <div class="detail-grid">
              <detail-field label="Paquetería" :value="order.carrier ?? '-'" />
              <detail-field label="URL de rastreo">
                <a
                  v-if="order.tracking_number"
                  class="detail-link"
                  :href="order.tracking_number"
                  target="_blank"
                  >{{ order.tracking_number ?? "-" }}</a
                >
                <p v-else class="detail-value">-</p>
              </detail-field>
            </div>
          </div>

          <button
            class="secondary-button my-2"
            @click.prevent="showTrackingInfoModal = true"
          >
            Actualizar
          </button>
        </div>

        <div
          class="mt-2 text-sm"
          v-if="
            order.related_shipment_orders &&
            order.related_shipment_orders.length > 0
          "
        >
          <p class="font-medium">
            Esta orden debe enviarse con las siguientes órdenes:
          </p>
          <div class="flex space-x-2">
            <p
              v-for="relatedOrder in order.related_shipment_orders"
              :key="relatedOrder.id"
              class="text-sm text-content-400"
            >
              #{{ relatedOrder.id }}
            </p>
          </div>
        </div>
      </div>

      <p v-if="order.diet && order.diet.pet" class="font-medium text-primary-500 mt-2">
        {{ order.diet.pet.name }}
      </p>

      <p class="font-medium">Dieta</p>

      <diet :diet="order.diet" :weeks="order.weeks" />

      <p class="font-medium mt-2">Paquetes</p>

      <meal-set
        v-for="(mealSet, index) in order.meal_sets"
        :key="mealSet.id"
        :index="index + 1"
        :mealSet="mealSet"
        :editEnabled="order.status === 'pending'"
        :addPrint="order.status === 'in_process'"
        @edit="$emit('updateMealSet', mealSet)"
        @printMeal="$emit('printMeal', $event)"
      />

      <p class="font-medium mt-4">Inventario Requerido</p>

      <div v-for="category in order.summary" :key="category.id" class="my-2">
        <p class="font-medium">{{ category.name }}</p>

        <table class="table-auto mt-2 bg-white">
          <tbody>
            <tr
              v-for="subcategory in category.subcategories"
              :key="subcategory.id"
              class="border"
            >
              <td class="p-2 border-r">
                {{ subcategory.name }}
              </td>
              <td class="p-2 border-r">
                {{ subcategory.totalQuantity.toLocaleString() }}g
              </td>
              <td v-if="order.status === 'pending'" class="p-2">
                <span
                  :class="{
                    'text-success-500': subcategory.available,
                  }"
                  class="text-error-500"
                  >{{
                    subcategory.available
                      ? "Disponible en inventario"
                      : "No disponible en inventario"
                  }}</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <slot />
    </div>
  </div>
</template>

<script>
import MealSet from "../models/MealSet";
import Diet from "../models/Diet";
import AddressComponent from "../ui/Address";
import TrackingInfoModal from "../modals/TrackingInfoModal";
import { formatDatetime, formatDate } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Pending Order",

  components: {
    MealSet,
    Diet,
    AddressComponent,
    TrackingInfoModal,
  },

  data() {
    return {
      collapsed: true,
      showTrackingInfoModal: false,
    };
  },

  props: {
    order: Object,
  },

  methods: {
    formatDatetime,

    formatDate,

    integerToCurrency,

    getStatus(status) {
      switch (status) {
        case "pending":
          return "Pendiente";
        case "in_process":
          return "En Proceso";
        case "ready_for_pickup":
          return "Lista para Pick-Up";
        case "ready_for_delivery":
          return "Lista para Enviar";
        case "delivered":
          return this.order.pickup_delivery ? "Entregada" : "Enviada";
        case "failed_to_send":
          return "Fallida al Enviar";
        default:
          return "";
      }
    },

    getBackgroundColor() {
      switch (this.order.status) {
        case "pending":
          return "bg-pending";
        case "in_process":
          return "bg-inProcess";
        case "ready_for_pickup":
          return "bg-ready";
        case "ready_for_delivery":
          return "bg-ready";
        case "delivered":
          return "bg-delivered";
        case "failed_to_send":
          return "bg-failed";
        default:
          return "";
      }
    },
  },
};
</script>